import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Code2, Rocket, Target, ArrowRight, CheckCircle2, Menu, X, Terminal, GitBranch, Database } from 'lucide-react';
import HomePage from './pages/HomePage';
import About from './pages/About';
import Solutions from './pages/Solutions';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import CaseStudies from './pages/CaseStudies';
import Industries from './pages/Industries';
import Approach from './pages/Approach';
import Careers from './pages/Careers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Documentation from './pages/Documentation';
import Blog from './pages/Blog';
import DaaS from './pages/DaaS';
import Projects from './pages/Projects';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isPastHero, setIsPastHero] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      if (isHomePage) {
        const heroLogo = document.getElementById('hero-logo');
        if (heroLogo) {
          const rect = heroLogo.getBoundingClientRect();
          setIsPastHero(rect.bottom < 0);
        }
      }
      
      const sections = ['solutions', 'approach', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    // Handle hash navigation after page load
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Add highlight class
          element.classList.add('bg-yellow-50');
          // Remove highlight after animation
          setTimeout(() => {
            element.classList.remove('bg-yellow-50');
          }, 2000);
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }

    // Close mobile menu when route changes
    setIsMenuOpen(false);
  }, [location]);

  const showLogo = !isHomePage || (isHomePage && isPastHero);

  const products = [
    {
      name: "Device Uptime",
      url: "https://deviceuptime.com/",
      description: "Monitor on-prem and cloud devices and services"
    },
    {
      name: "Drive the Demand",
      url: "https://drivethedemand.com/",
      description: "Easy-to-use email marketing"
    },
    {
      name: "SureTrack CRM",
      url: "https://suretrackcrm.com/?utm_source=StingerBuilds&utm_medium=Link",
      description: "SureTrack CRM is the complete solution for managing customer relationships, with powerful tools and AI-driven insights that help businesses grow"
    },
    {
      name: "Standup Feed",
      url: "https://standupfeed.com",
      description: "Stay aligned without the meeting"
    },
    {
      name: "ClientCliq",
      url: "https://clientcliq.com",
      description: "Coming Soon"
    }
  ];

  const solutionsLinks = [
    { text: "Custom Software Development", hash: "custom-software" },
    { text: "Web Development", hash: "web-development" },
    { text: "Mobile App Development", hash: "mobile-app" },
    { text: "Software Integration", hash: "software-integration" },
    { text: "DevOps & Automation", hash: "devops" },
    { text: "API Development", hash: "api-development" },
    { text: "Enterprise Software", hash: "enterprise-software" },
    { text: "UI/UX Design", hash: "ui-ux-design" },
    { text: "Maintenance & Support", hash: "maintenance" },
    { text: "Quality Assurance", hash: "quality-assurance" },
    { text: "SaaS Development", hash: "saas-development" },
    { text: "IoT Development", hash: "iot-development" },
    { text: "Hosting Solutions", hash: "hosting" },
    { text: "Technology Consulting", hash: "tech-consulting" }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Link to="/">
                <img 
                  src="https://stingerbuilds.com/Stinger-builds-software-logo-transparent.png"
                  alt="Stinger Builds"
                  className={`h-12 w-auto transition-all duration-300 ${showLogo ? 'opacity-100' : 'opacity-0'}`}
                />
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-all text-white hover:text-yellow-400`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`text-sm font-medium transition-all text-white hover:text-yellow-400`}
              >
                About
              </Link>
              <Link
                to="/solutions"
                className={`text-sm font-medium transition-all text-white hover:text-yellow-400`}
              >
                Solutions
              </Link>
              <Link
                to="/projects"
                className={`text-sm font-medium transition-all text-white hover:text-yellow-400`}
              >
                Precision Projects
              </Link>
              <Link
                to="/daas"
                className={`text-sm font-medium transition-all text-white hover:text-yellow-400`}
              >
                Development as a Service
              </Link>
              <Link
                to="/contact"
                className={`text-sm font-medium transition-all text-white hover:text-yellow-400`}
              >
                Contact
              </Link>
              <Link
                to="/contact"
                className="px-4 py-2 bg-yellow-400 text-slate-900 rounded-lg font-medium hover:bg-yellow-300 transition-all"
              >
                Get Started
              </Link>
            </div>

            <button
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          <div 
            className={`md:hidden fixed inset-x-0 top-[76px] transition-all duration-300 ${
              isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
            }`}
          >
            <div className="bg-slate-900/95 backdrop-blur-sm shadow-lg border-t border-slate-800">
              <div className="container mx-auto px-6 py-6 space-y-4">
                <Link
                  to="/"
                  className="block text-lg text-white hover:text-yellow-400 transition py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="block text-lg text-white hover:text-yellow-400 transition py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/solutions"
                  className="block text-lg text-white hover:text-yellow-400 transition py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Solutions
                </Link>
                <Link
                  to="/projects"
                  className="block text-lg text-white hover:text-yellow-400 transition py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Precision Projects
                </Link>
                <Link
                  to="/daas"
                  className="block text-lg text-white hover:text-yellow-400 transition py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Development as a Service
                </Link>
                <Link
                  to="/contact"
                  className="block text-lg text-white hover:text-yellow-400 transition py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Link>
                <Link
                  to="/contact"
                  className="block text-lg bg-yellow-400 text-slate-900 rounded-lg font-medium hover:bg-yellow-300 transition-all py-3 px-4 text-center mt-6"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/daas" element={<DaaS />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/approach" element={<Approach />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <footer className="bg-slate-900 text-gray-400 py-12 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <h3 className="text-white font-semibold mb-6">Our Products</h3>
              <ul className="space-y-4">
                {products.map((product, index) => (
                  <li key={index}>
                    <div className="group relative inline-block">
                      <a 
                        href={product.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:text-yellow-400 transition-colors"
                      >
                        {product.name}
                      </a>
                      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 p-2 bg-slate-800 text-sm text-gray-300 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        {product.description}
                        <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 transform rotate-45 w-2 h-2 bg-slate-800"></div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {[
              {
                title: "Solutions",
                links: solutionsLinks
              },
              {
                title: "Company",
                links: [
                  { text: "About Us", path: "/about" },
                  { text: "Solutions", path: "/solutions" },
                  { text: "Projects", path: "/projects" },
                  { text: "DaaS", path: "/daas" },
                  { text: "Industries", path: "/industries" },
                  { text: "Case Studies", path: "/case-studies" },
                  { text: "Approach", path: "/approach" },
                  { text: "Careers", path: "/careers" },
                  { text: "Blog", path: "/blog" },
                  { text: "Contact", path: "/contact" }
                ]
              },
              {
                title: "Resources",
                links: [
                  { text: "Documentation", path: "/documentation" },
                  { text: "FAQ", path: "/contact#faq" },
                  { text: "Privacy Policy", path: "/privacy-policy" }
                ]
              }
            ].map((column, index) => (
              <div key={index}>
                <h3 className="text-white font-semibold mb-6">{column.title}</h3>
                <ul className="space-y-4">
                  {column.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      {'path' in link ? (
                        <Link
                          to={link.path}
                          className="hover:text-yellow-400 transition-colors"
                        >
                          {link.text}
                        </Link>
                      ) : (
                        <Link
                          to={`/solutions#${link.hash}`}
                          className="hover:text-yellow-400 transition-colors"
                        >
                          {link.text}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} Stinger Builds. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <Link to="/terms" className="hover:text-yellow-400 transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-yellow-400 transition-colors">Privacy</Link>
              <Link to="/privacy-policy#cookies" className="hover:text-yellow-400 transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;